import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { ticketService } from '../../services/questService';
import AttachmentUploadDialog from './AttachmentUploadDialog';
import {
  QUEST_STATUS,
  getQuestStatusIndex,
  getStatusChipConfig,
  normalizeQuestStatus,
} from '../../utils/questStatus';
import { MAX_ATTACHMENT_BYTES, tooLargeAttachmentMessage } from '../../utils/attachmentSize';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const getMasterName = (m) =>
  [m.lastName, m.firstName, m.patronymic].filter(Boolean).join(' ') || m.name || m.login || '—';

/** ID вложения из GET /quests/{id} (attachmentId; fileId — устаревшее) */
export const getAttachmentId = (attachment) => attachment?.attachmentId ?? attachment?.fileId ?? '';

const getAttachmentUserRole = (attachment) => attachment?.userRole ?? attachment?.UserRole ?? '';

/** Вложения диспетчера; без userRole — legacy, считаем диспетчерскими */
export const isDispatcherAttachment = (attachment) => {
  const role = getAttachmentUserRole(attachment);
  return !role || role === 'Dispatcher';
};

export const isMasterAttachment = (attachment) => getAttachmentUserRole(attachment) === 'Master';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

const formatQuestLineDate = (dateString) => {
  if (!dateString || String(dateString).startsWith('0001-')) return null;
  try {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return null;
  }
};

const formatQuestNumberLine = (quest, showCompletionDate = false) => {
  const num = quest.number || quest.id;
  const created = quest.createdAt || quest.createdDate;
  const createdDate = formatQuestLineDate(created);
  const completionDate = showCompletionDate
    ? formatQuestLineDate(quest.finishedAt)
    : null;

  return [
    `№${num}`,
    createdDate ? `от ${createdDate}` : null,
    completionDate ? `завершена: ${completionDate}` : null,
  ].filter(Boolean).join(' ');
};

const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocal = (value) => (value ? new Date(value).toISOString() : '');

const draftFromQuest = (quest) => ({
  title: quest.title || '',
  clientName: quest.clientName || '',
  accountNumber: quest.accountNumber || quest.account || '',
  phoneNumber: quest.phoneNumber || quest.phone || '',
  address: quest.address || '',
  serviceId: quest.serviceId || '',
  deadlineLocal: toDatetimeLocal(quest.deadline),
  fixedPrice: quest.fixedPrice != null && quest.fixedPrice !== '' ? String(quest.fixedPrice) : '',
  description: quest.description || '',
  clientComment: quest.clientComment || quest.comment || '',
});

const getAttachmentFileIds = (quest) => {
  const fromList = (quest.attachments || [])
    .filter(isDispatcherAttachment)
    .map((a) => getAttachmentId(a))
    .filter(Boolean);
  if (fromList.length) return fromList;
  return quest.attachmentFileIds || [];
};

export const buildQuestPutPayload = (quest, overrides = {}) => {
  const { status: overrideStatus, ...restOverrides } = overrides;
  const status =
    overrideStatus !== undefined
      ? normalizeQuestStatus(overrideStatus)
      : normalizeQuestStatus(quest);

  return {
    masterId: quest.masterId || EMPTY_GUID,
    serviceId: quest.serviceId || EMPTY_GUID,
    title: quest.title || '',
    description: quest.description || '',
    deadline: quest.deadline || new Date().toISOString(),
    address: quest.address || '',
    phoneNumber: quest.phoneNumber || quest.phone || '',
    clientName: quest.clientName || '',
    clientComment: quest.clientComment || quest.comment || '',
    accountNumber: quest.accountNumber || quest.account || '',
    fixedPrice: Number(quest.fixedPrice) || 0,
    attachmentFileIds: getAttachmentFileIds(quest),
    status,
    ...restOverrides,
  };
};

export const buildQuestPutPayloadFromDraft = (quest, draft) =>
  buildQuestPutPayload(quest, {
    title: draft.title,
    description: draft.description,
    deadline: fromDatetimeLocal(draft.deadlineLocal) || quest.deadline,
    address: draft.address,
    phoneNumber: draft.phoneNumber,
    clientName: draft.clientName,
    clientComment: draft.clientComment,
    accountNumber: draft.accountNumber,
    serviceId: draft.serviceId || quest.serviceId || EMPTY_GUID,
    fixedPrice: Number(draft.fixedPrice) || 0,
  });

const draftsEqual = (a, b) =>
  a.title === b.title &&
  a.clientName === b.clientName &&
  a.accountNumber === b.accountNumber &&
  a.phoneNumber === b.phoneNumber &&
  a.address === b.address &&
  a.serviceId === b.serviceId &&
  a.deadlineLocal === b.deadlineLocal &&
  a.fixedPrice === b.fixedPrice &&
  a.description === b.description &&
  a.clientComment === b.clientComment;

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    color="text.secondary"
    sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', fontWeight: 600 }}
  >
    {children}
  </Typography>
);

const MasterAttachmentRow = ({ attachment }) => {
  const attachmentId = getAttachmentId(attachment);
  const fileName = attachment.fileName || attachmentId;
  const url = attachment.url;

  const handleView = () => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownload = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        bgcolor: 'grey.50',
        borderRadius: 1,
      }}
    >
      <AttachFileIcon fontSize="small" color="action" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {fileName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {formatDate(attachment.uploadedAt)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
        {url && (
          <Tooltip title="Просмотр">
            <IconButton size="small" onClick={handleView} aria-label="Просмотр">
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {url && (
          <Tooltip title="Скачать">
            <IconButton size="small" onClick={handleDownload} aria-label="Скачать">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const QuestDetailPanel = ({
  quest,
  currentTab,
  onSave,
  onAssign,
  onCancel,
  onExecutorChange,
  onQuestChanged,
  onNotify,
  beforeQuestChangeRef,
}) => {
  const { masters, services } = useSelector((state) => state.quests);
  const [draft, setDraft] = useState(() => (quest ? draftFromQuest(quest) : null));
  const [savedDraft, setSavedDraft] = useState(() => (quest ? draftFromQuest(quest) : null));
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [executorSaving, setExecutorSaving] = useState(false);
  const [showPrefillHint, setShowPrefillHint] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadingDrop, setUploadingDrop] = useState(false);
  const [fileSizeWarning, setFileSizeWarning] = useState('');
  const [dirtyDialog, setDirtyDialog] = useState(null);

  const questIdRef = useRef(quest?.id);

  const statusIndex = quest ? getQuestStatusIndex(quest) : null;
  const isCompletedTab = currentTab === getQuestStatusIndex(QUEST_STATUS.COMPLETED);
  const isReadOnly = statusIndex === getQuestStatusIndex(QUEST_STATUS.CANCELLED);
  const isDirty = draft && savedDraft ? !draftsEqual(draft, savedDraft) : false;
  const statusConfig = quest ? getStatusChipConfig(quest) : null;

  const resetDraftFromQuest = useCallback((q) => {
    const next = draftFromQuest(q);
    setDraft(next);
    setSavedDraft(next);
    setShowPrefillHint(Boolean(q.accountNumber || q.account));
  }, []);

  useEffect(() => {
    if (!quest) {
      setDraft(null);
      setSavedDraft(null);
      questIdRef.current = null;
      return;
    }
    if (quest.id !== questIdRef.current) {
      questIdRef.current = quest.id;
      resetDraftFromQuest(quest);
      setFileSizeWarning('');
      return;
    }
    if (!isDirty || getQuestStatusIndex(quest) === getQuestStatusIndex(QUEST_STATUS.CANCELLED)) {
      resetDraftFromQuest(quest);
    }
  }, [quest, isDirty, resetDraftFromQuest]);

  useEffect(() => {
    if (!beforeQuestChangeRef) return undefined;
    beforeQuestChangeRef.current = (nextQuest) =>
      new Promise((resolve) => {
        if (!isDirty || !quest?.id || nextQuest?.id === quest.id) {
          resolve(true);
          return;
        }
        setDirtyDialog({ nextQuest, resolve });
      });
    return () => {
      beforeQuestChangeRef.current = null;
    };
  }, [beforeQuestChangeRef, isDirty, quest?.id]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isDirty && !isReadOnly && !saving) {
          handleSaveClick();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isReadOnly, saving, draft, quest]);

  const handleDraftChange = (field, value) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'serviceId') {
        const selectedService = services?.find((s) => s.id === value);
        if (selectedService?.price != null && selectedService.price !== '') {
          next.fixedPrice = String(selectedService.price);
        }
      }
      return next;
    });
  };

  const handleSaveClick = async () => {
    if (!quest || !draft || !onSave || isReadOnly) return;
    setSaving(true);
    try {
      const payload = buildQuestPutPayloadFromDraft(quest, draft);
      await onSave(quest.id, payload);
      setSavedDraft({ ...draft });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignClick = async () => {
    if (!quest || !onAssign || statusIndex !== getQuestStatusIndex(QUEST_STATUS.NEW)) return;
    setAssigning(true);
    try {
      await onAssign(quest);
    } finally {
      setAssigning(false);
    }
  };

  const handleCancelClick = async () => {
    if (!quest || !onCancel || statusIndex === getQuestStatusIndex(QUEST_STATUS.CANCELLED)) return;
    if (!window.confirm(`Вы уверены, что хотите отменить заявку №${quest.number || quest.id}?`)) return;
    setCancelling(true);
    try {
      await onCancel(quest);
    } finally {
      setCancelling(false);
    }
  };

  const handleExecutorChange = async (masterId) => {
    if (!quest || !onExecutorChange || isReadOnly) return;
    if (masterId === (quest.masterId || '')) return;
    setExecutorSaving(true);
    try {
      await onExecutorChange(quest.id, masterId);
    } finally {
      setExecutorSaving(false);
    }
  };

  const handleCopyNumber = async () => {
    const text = String(quest.number || quest.id);
    try {
      await navigator.clipboard.writeText(text);
      if (onNotify) onNotify('Номер скопирован', 'success');
    } catch {
      if (onNotify) onNotify('Не удалось скопировать', 'error');
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Удалить вложение?')) return;
    setDeletingId(attachmentId);
    try {
      await ticketService.deleteAttachment(quest.id, attachmentId);
      if (onQuestChanged) onQuestChanged();
      if (onNotify) onNotify('Вложение удалено', 'success');
    } catch {
      if (onNotify) onNotify('Ошибка удаления вложения', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const uploadFiles = async (files) => {
    if (!files?.length || !quest?.id || isReadOnly) return;
    setUploadingDrop(true);
    try {
      for (const file of files) {
        await ticketService.uploadAttachment(quest.id, file);
      }
      if (onQuestChanged) onQuestChanged();
      if (onNotify) onNotify('Вложения загружены', 'success');
    } catch {
      if (onNotify) onNotify('Ошибка загрузки вложений', 'error');
    } finally {
      setUploadingDrop(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted, fileRejections) => {
      setFileSizeWarning(tooLargeAttachmentMessage(fileRejections));
      uploadFiles(accepted);
    },
    maxSize: MAX_ATTACHMENT_BYTES,
    disabled: isReadOnly || uploadingDrop,
    noClick: isReadOnly,
    multiple: true,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp'],
      'application/*': ['.pdf', '.doc', '.docx', '.txt'],
    },
  });

  const handleDirtyDialog = async (action) => {
    if (!dirtyDialog) return;
    const { resolve } = dirtyDialog;
    setDirtyDialog(null);

    if (action === 'cancel') {
      resolve(false);
      return;
    }

    if (action === 'save') {
      setSaving(true);
      try {
        const payload = buildQuestPutPayloadFromDraft(quest, draft);
        await onSave(quest.id, payload);
        setSavedDraft({ ...draft });
        resolve(true);
      } catch {
        resolve(false);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (action === 'discard') {
      resolve(true);
    }
  };

  const serviceOptions = useMemo(() => services || [], [services]);
  const allAttachments = quest?.attachments || [];
  const dispatcherAttachments = useMemo(
    () => allAttachments.filter(isDispatcherAttachment),
    [allAttachments],
  );
  const masterAttachments = useMemo(
    () => allAttachments.filter(isMasterAttachment),
    [allAttachments],
  );
  const completionComment = quest?.completionComment?.trim();

  if (!quest) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4 }}>
        <Typography color="text.secondary">Выберите заявку для просмотра</Typography>
      </Box>
    );
  }

  if (!draft) return null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
      {/* Sticky header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: { xs: 2, md: 3 },
          py: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 200 }}>
            <Chip
              label={statusConfig.label}
              color={statusConfig.color}
              size="small"
              sx={{ fontWeight: 600, flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                value={draft.title}
                onChange={(e) => handleDraftChange('title', e.target.value)}
                variant="standard"
                fullWidth
                disabled={isReadOnly}
                placeholder="Название заявки"
                InputProps={{
                  sx: { fontSize: '1.25rem', fontWeight: 600 },
                  disableUnderline: !isReadOnly,
                }}
                sx={{ mb: 0.5 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {formatQuestNumberLine(quest, isCompletedTab)}
                </Typography>
                <Tooltip title="Скопировать номер">
                  <IconButton size="small" onClick={handleCopyNumber} aria-label="Скопировать номер">
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {isDirty && (
              <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                Есть несохранённые изменения
              </Typography>
            )}
            {statusIndex === getQuestStatusIndex(QUEST_STATUS.NEW) && (
              <Button
                variant="contained"
                onClick={handleAssignClick}
                disabled={assigning || isReadOnly}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {assigning ? <CircularProgress size={20} color="inherit" /> : 'Назначить'}
              </Button>
            )}
            {statusIndex !== getQuestStatusIndex(QUEST_STATUS.CANCELLED) && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancelClick}
                disabled={cancelling}
                sx={{ textTransform: 'none' }}
              >
                {cancelling ? <CircularProgress size={20} color="inherit" /> : 'Отменить заявку'}
              </Button>
            )}
            {!isReadOnly && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                disabled={!isDirty || saving}
                sx={{ textTransform: 'none' }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Сохранить'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Body — CSS Grid: правая колонка только у «Клиент»+«Заявка», вложения сразу под заявкой */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, md: 3 }, py: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            alignItems: 'start',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(260px, 38%)' },
          }}
        >
          <Box sx={{ gridColumn: 1 }}>
            <SectionTitle>Клиент</SectionTitle>
              {showPrefillHint && !isReadOnly && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                  Данные подставлены из системы. Проверьте и при необходимости измените перед сохранением.
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Лицевой счёт"
                    fullWidth
                    size="small"
                    value={draft.accountNumber}
                    onChange={(e) => handleDraftChange('accountNumber', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="ФИО клиента"
                    fullWidth
                    size="small"
                    value={draft.clientName}
                    onChange={(e) => handleDraftChange('clientName', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Телефон"
                    fullWidth
                    size="small"
                    value={draft.phoneNumber}
                    onChange={(e) => handleDraftChange('phoneNumber', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Адрес"
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={draft.address}
                    onChange={(e) => handleDraftChange('address', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
              </Grid>
          </Box>

          <Box sx={{ gridColumn: 1 }}>
            <SectionTitle>Заявка</SectionTitle>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth size="small" disabled={isReadOnly}>
                    <InputLabel>Вид заявки</InputLabel>
                    <Select
                      value={draft.serviceId || ''}
                      label="Вид заявки"
                      onChange={(e) => handleDraftChange('serviceId', e.target.value)}
                    >
                      {serviceOptions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Срок выполнения"
                    type="datetime-local"
                    fullWidth
                    size="small"
                    value={draft.deadlineLocal}
                    onChange={(e) => handleDraftChange('deadlineLocal', e.target.value)}
                    disabled={isReadOnly}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Стоимость, ₽"
                    fullWidth
                    size="small"
                    type="number"
                    value={draft.fixedPrice}
                    onChange={(e) => handleDraftChange('fixedPrice', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    label="Описание"
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    value={draft.description}
                    onChange={(e) => handleDraftChange('description', e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>
              </Grid>
          </Box>

          <Box sx={{ gridColumn: { xs: 1, md: 2 }, gridRow: { xs: 'auto', md: '1 / 3' } }}>
            <Box sx={{ mb: 3 }}>
              <SectionTitle>Исполнитель</SectionTitle>
              <FormControl fullWidth size="small" disabled={isReadOnly || executorSaving}>
                <InputLabel>Исполнитель</InputLabel>
                <Select
                  value={quest.masterId || ''}
                  label="Исполнитель"
                  onChange={(e) => handleExecutorChange(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Не назначен</em>
                  </MenuItem>
                  {masters?.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {getMasterName(m)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {executorSaving && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Сохранение…
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <SectionTitle>Комментарий для исполнителя</SectionTitle>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Видит исполнитель
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={4}
                value={draft.clientComment}
                onChange={(e) => handleDraftChange('clientComment', e.target.value)}
                disabled={isReadOnly}
                placeholder="Комментарий для исполнителя"
              />
            </Box>

            <Box sx={{ mb: completionComment ? 3 : 0 }}>
              <SectionTitle>Вложения исполнителя</SectionTitle>
              {masterAttachments.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {masterAttachments.map((att) => (
                    <MasterAttachmentRow key={getAttachmentId(att)} attachment={att} />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Нет вложений
                </Typography>
              )}
            </Box>

            {completionComment && (
              <Box>
                <SectionTitle>Комментарий исполнителя при завершении</SectionTitle>
                <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1.5 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {completionComment}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ gridColumn: 1, minWidth: 0 }}>
            <SectionTitle>Вложения</SectionTitle>
            {dispatcherAttachments.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                {dispatcherAttachments.map((att) => {
                  const attachmentId = getAttachmentId(att);
                  return (
                  <Box
                    key={attachmentId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <AttachFileIcon fontSize="small" color="action" />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Link
                        href={att.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="body2"
                        sx={{ wordBreak: 'break-word' }}
                      >
                        {att.fileName || attachmentId}
                      </Link>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {formatDate(att.uploadedAt)}
                      </Typography>
                    </Box>
                    {!isReadOnly && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteAttachment(attachmentId)}
                        disabled={deletingId === attachmentId}
                        color="error"
                        title="Удалить"
                      >
                        {deletingId === attachmentId ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </Box>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Нет вложений
              </Typography>
            )}
            {!isReadOnly && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setUploadDialogOpen(true)}
                sx={{ mb: 2, textTransform: 'none', display: 'block' }}
              >
                Добавить вложение
              </Button>
            )}
            {!isReadOnly && fileSizeWarning && (
              <Alert
                severity="warning"
                onClose={() => setFileSizeWarning('')}
                sx={{ mb: 2, borderRadius: 2 }}
              >
                {fileSizeWarning}
              </Alert>
            )}
            {!isReadOnly && (
              <Box
                {...getRootProps()}
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: '2px dashed',
                  borderRadius: 2,
                  borderColor: isDragActive ? 'primary.main' : 'divider',
                  bgcolor: isDragActive ? 'action.hover' : 'grey.50',
                }}
              >
                <input {...getInputProps()} />
                {uploadingDrop ? (
                  <CircularProgress size={28} />
                ) : (
                  <>
                    <CloudUploadIcon sx={{ color: 'text.disabled', mb: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Перетащите файлы или нажмите для выбора
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.disabled' }}>
                      JPG, PNG, GIF, PDF, DOC, DOCX, TXT (макс. 20 МБ)
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <AttachmentUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        questId={quest?.id}
        onUploadSuccess={() => {
          setUploadDialogOpen(false);
          if (onQuestChanged) onQuestChanged();
          if (onNotify) onNotify('Вложения загружены', 'success');
        }}
      />

      <Dialog open={Boolean(dirtyDialog)} onClose={() => handleDirtyDialog('cancel')} maxWidth="xs" fullWidth>
        <DialogTitle>Несохранённые изменения</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Сохранить изменения перед переходом к другой заявке?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => handleDirtyDialog('cancel')} sx={{ textTransform: 'none' }}>
            Отмена
          </Button>
          <Button onClick={() => handleDirtyDialog('discard')} color="inherit" sx={{ textTransform: 'none' }}>
            Не сохранять
          </Button>
          <Button variant="contained" onClick={() => handleDirtyDialog('save')} sx={{ textTransform: 'none' }}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuestDetailPanel;
